import { useState, useCallback, useEffect, useRef } from 'react'
import type { App, Folder, FolderApp } from '../../lib/api'
import {
  listFolders,
  createFolder as apiCreateFolder,
  updateFolder,
  deleteFolder as apiDeleteFolder,
  reorderFolders as apiReorderFolders,
  listFolderApps,
  addAppToFolder as apiAddAppToFolder,
  removeAppFromFolder as apiRemoveAppFromFolder,
} from '../../lib/api'

const COLLAPSED_KEY = 'myapps-folders-collapsed'

export interface UseFoldersReturn {
  folders: Folder[]
  activeFolderId: number | null
  activeFolderApps: App[]
  loading: boolean
  collapsed: boolean
  setCollapsed: (v: boolean) => void
  navigateToFolder: (id: number) => Promise<void>
  navigateToRoot: () => void
  createFolder: (name: string, color?: string) => Promise<Folder>
  renameFolder: (id: number, name: string) => Promise<void>
  changeFolderColor: (id: number, color: string) => Promise<void>
  deleteFolder: (id: number) => Promise<void>
  reorderFolders: (orderedIds: number[]) => Promise<void>
  addAppToFolder: (folderId: number, appName: string) => Promise<void>
  removeAppFromFolder: (folderId: number, appName: string) => Promise<void>
  moveAppsToFolder: (folderId: number, appNames: string[]) => Promise<void>
  getAppFolderIds: (appName: string) => number[]
  getFolderAppNames: (folderId: number) => string[]
  createFolderAndAddApp: (name: string, appName: string) => Promise<Folder>
  refreshFolders: () => Promise<void>
}

/**
 * Hook for managing folder state: CRUD, navigation, membership, and collapse persistence.
 */
export function useFolders(allApps: App[], context: string = 'my-apps'): UseFoldersReturn {
  const [folders, setFolders] = useState<Folder[]>([])
  const [activeFolderId, setActiveFolderId] = useState<number | null>(null)
  const [activeFolderApps, setActiveFolderApps] = useState<App[]>([])
  const [loading, setLoading] = useState(true)
  const [collapsed, setCollapsedState] = useState(
    () => localStorage.getItem(`${COLLAPSED_KEY}-${context}`) === 'true'
  )
  const [appFolderMap, setAppFolderMap] = useState<Map<string, Set<number>>>(new Map())
  const deletedIdsRef = useRef(new Set<number>())
  const previousAppNamesRef = useRef(new Map<number, string>())

  const setCollapsed = useCallback((v: boolean) => {
    setCollapsedState(v)
    localStorage.setItem(`${COLLAPSED_KEY}-${context}`, String(v))
  }, [context])

  const refreshFolders = useCallback(async () => {
    try {
      const data = await listFolders(context)
      setFolders(data)
    } catch {
      // Silently fail - folders are not critical
    }
  }, [context])

  // Initial load
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const data = await listFolders(context)
        if (!cancelled) {
          setFolders(data)
          // Load app→folder memberships
          const map = new Map<string, Set<number>>()
          for (const folder of data) {
            try {
              const folderApps = await listFolderApps(folder.id)
              for (const fa of folderApps) {
                if (!map.has(fa.name)) map.set(fa.name, new Set())
                map.get(fa.name)!.add(folder.id)
              }
            } catch { /* skip */ }
          }
          if (!cancelled) setAppFolderMap(map)
        }
      } catch {
        // Silently fail
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [context])

  useEffect(() => {
    const currentAppNames = new Map(allApps.map((app) => [app.id, app.name]))

    setAppFolderMap((prev) => {
      let next = prev

      for (const [id, nextName] of currentAppNames.entries()) {
        const previousName = previousAppNamesRef.current.get(id)
        if (!previousName || previousName === nextName || !prev.has(previousName)) {
          continue
        }

        if (next === prev) {
          next = new Map(prev)
        }

        const previousFolders = next.get(previousName)
        const mergedFolders = new Set([
          ...(next.get(nextName) ?? []),
          ...(previousFolders ?? []),
        ])
        next.delete(previousName)
        next.set(nextName, mergedFolders)
      }

      return next
    })

    setActiveFolderApps((prev) => {
      if (prev.length === 0) return prev
      const latestAppsById = new Map(allApps.map((app) => [app.id, app]))
      return prev.flatMap((folderApp) => {
        const latest = latestAppsById.get(folderApp.id)
        return latest ? [latest] : []
      })
    })

    previousAppNamesRef.current = currentAppNames
  }, [allApps])

  const resolveFolderAppsFromApi = useCallback(
    (folderApps: FolderApp[]): App[] => {
      const appsByName = new Map(allApps.map((a) => [a.name, a]))
      return folderApps.map((fa) => {
        const fullApp = appsByName.get(fa.name)
        if (fullApp) return fullApp
        return {
          id: fa.id,
          name: fa.name,
          display_name: fa.display_name,
          description: fa.description,
          status: fa.status || 'stopped',
          port: 0,
          runtime: 'node',
          access_level: 'personal',
          git_initialized: false,
          url: '',
          workspace_url: '',
          created_at: fa.added_at,
          updated_at: null,
          last_accessed: null,
          keep_alive: false,
        } as App
      })
    },
    [allApps]
  )

  const navigateToFolder = useCallback(
    async (id: number) => {
      if (deletedIdsRef.current.has(id)) return
      setActiveFolderId(id)
      try {
        const folderApps = await listFolderApps(id)
        setActiveFolderApps(resolveFolderAppsFromApi(folderApps))
      } catch {
        setActiveFolderApps([])
      }
    },
    [resolveFolderAppsFromApi]
  )

  const navigateToRoot = useCallback(() => {
    setActiveFolderId(null)
    setActiveFolderApps([])
  }, [])

  const createFolder = useCallback(
    async (name: string, color?: string): Promise<Folder> => {
      const folder = await apiCreateFolder(name, color, context)
      setFolders((prev) => [...prev, folder])
      return folder
    },
    [context]
  )

  const renameFolder = useCallback(async (id: number, name: string) => {
    const updated = await updateFolder(id, { name })
    setFolders((prev) => prev.map((f) => (f.id === id ? updated : f)))
  }, [])

  const changeFolderColor = useCallback(async (id: number, color: string) => {
    const updated = await updateFolder(id, { color })
    setFolders((prev) => prev.map((f) => (f.id === id ? updated : f)))
  }, [])

  const deleteFolderFn = useCallback(
    async (id: number) => {
      deletedIdsRef.current.add(id)
      await apiDeleteFolder(id)
      setFolders((prev) => prev.filter((f) => f.id !== id))
      if (activeFolderId === id) {
        setActiveFolderId(null)
        setActiveFolderApps([])
      }
    },
    [activeFolderId]
  )

  const reorderFoldersFn = useCallback(async (orderedIds: number[]) => {
    setFolders((prev) => {
      const folderMap = new Map(prev.map((f) => [f.id, f]))
      return orderedIds
        .map((id, idx) => {
          const folder = folderMap.get(id)
          return folder ? { ...folder, sort_order: idx * 1000 } : null
        })
        .filter((f): f is Folder => f !== null)
    })
    try {
      await apiReorderFolders(orderedIds)
    } catch {
      await refreshFolders()
    }
  }, [refreshFolders])

  const addAppToFolderFn = useCallback(
    async (folderId: number, appName: string) => {
      const result = await apiAddAppToFolder(folderId, appName)
      setAppFolderMap(prev => {
        const next = new Map(prev)
        const set = new Set(next.get(appName) || [])
        set.add(folderId)
        next.set(appName, set)
        return next
      })
      if (result.added) {
        setFolders((prev) =>
          prev.map((f) =>
            f.id === folderId ? { ...f, app_count: f.app_count + 1 } : f
          )
        )
      }
      if (activeFolderId === folderId) {
        try {
          const folderApps = await listFolderApps(folderId)
          setActiveFolderApps(resolveFolderAppsFromApi(folderApps))
        } catch {
          // Ignore refetch error
        }
      }
    },
    [activeFolderId, resolveFolderAppsFromApi]
  )

  const removeAppFromFolderFn = useCallback(
    async (folderId: number, appName: string) => {
      const result = await apiRemoveAppFromFolder(folderId, appName)
      setAppFolderMap(prev => {
        const next = new Map(prev)
        const set = new Set(next.get(appName) || [])
        set.delete(folderId)
        if (set.size === 0) next.delete(appName)
        else next.set(appName, set)
        return next
      })
      if (result.removed) {
        setFolders((prev) =>
          prev.map((f) =>
            f.id === folderId
              ? { ...f, app_count: Math.max(0, f.app_count - 1) }
              : f
          )
        )
      }
      if (activeFolderId === folderId) {
        setActiveFolderApps((prev) => prev.filter((a) => a.name !== appName))
      }
    },
    [activeFolderId]
  )

  const moveAppsToFolder = useCallback(
    async (folderId: number, appNames: string[]) => {
      const results = await Promise.all(
        appNames.map((name) => apiAddAppToFolder(folderId, name))
      )
      const addedCount = results.filter((result) => result.added).length
      setAppFolderMap((prev) => {
        const next = new Map(prev)
        for (const appName of appNames) {
          const set = new Set(next.get(appName) || [])
          set.add(folderId)
          next.set(appName, set)
        }
        return next
      })
      if (addedCount > 0) {
        setFolders((prev) =>
          prev.map((f) =>
            f.id === folderId
              ? { ...f, app_count: f.app_count + addedCount }
              : f
          )
        )
      }
      if (activeFolderId === folderId) {
        try {
          const folderApps = await listFolderApps(folderId)
          setActiveFolderApps(resolveFolderAppsFromApi(folderApps))
        } catch {
          // Ignore refetch error
        }
      }
    },
    [activeFolderId, resolveFolderAppsFromApi]
  )

  const getAppFolderIds = useCallback(
    (appName: string): number[] => {
      return Array.from(appFolderMap.get(appName) || [])
    },
    [appFolderMap]
  )

  const getFolderAppNames = useCallback(
    (folderId: number): string[] => {
      return Array.from(appFolderMap.entries())
        .filter(([, folderIds]) => folderIds.has(folderId))
        .map(([appName]) => appName)
    },
    [appFolderMap]
  )

  const createFolderAndAddApp = useCallback(
    async (name: string, appName: string): Promise<Folder> => {
      const folder = await createFolder(name)
      await addAppToFolderFn(folder.id, appName)
      return folder
    },
    [createFolder, addAppToFolderFn]
  )

  return {
    folders,
    activeFolderId,
    activeFolderApps,
    loading,
    collapsed,
    setCollapsed,
    navigateToFolder,
    navigateToRoot,
    createFolder,
    renameFolder,
    changeFolderColor,
    deleteFolder: deleteFolderFn,
    reorderFolders: reorderFoldersFn,
    addAppToFolder: addAppToFolderFn,
    removeAppFromFolder: removeAppFromFolderFn,
    moveAppsToFolder,
    getAppFolderIds,
    getFolderAppNames,
    createFolderAndAddApp,
    refreshFolders,
  }
}
